<script lang="ts">
    import { computed, reactive, ref, onUnmounted, watchEffect, inject } from 'vue';
    import vm, { JourneyNode, tmp_uuidv4 } from '@journeys/Journeys.viewmodel';

    import { NodeType, NodeConditionVariation } from '@journeys/Enums';

    import { ConditionList, ConditionOptions } from '../Partials/ConditionList';

    // components
    import NodeTypeSegment from '../Partials/NodeTypeSegment.vue';
    import NodeTypeTask from '../Partials/NodeTypeTask.vue';
    import NodeTypeCondition from '../Partials/NodeTypeCondition.vue';
    import NodePresetList from './Partials/NodePresetList.vue';
    import FluidButton from '@shared/components/FluidButton.vue';
    import FluidSelect from '@shared/components/FluidSelect.vue';
    import FluidInput from '@shared/components/FluidInput.vue';
    import FluidTextarea from '@shared/components/FluidTextarea.vue';
    import NativeSelect from '@shared/components/NativeSelect.vue';
    import FluidToggle from '@shared/components/FluidToggle.vue';

    import BSFormInputText from '@shared/components/BSFormInputText.vue';
    import BSFormInputTextarea from '@shared/components/BSFormInputTextarea.vue';

    import { onMounted } from 'vue';
    import { vue_ko_sync } from '@shared/KoSync';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { FeatureService } from '@shared/directives/Feature.service';

    // this is mostly for debugging, otherwise components are Anonymous

    const component = {
        name: 'Tab_NodeSettings_Data',
    };
    export default {
        name: component.name,
        components: { NodeTypeSegment },
    };
</script>

<script lang="ts" setup>
    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const featureAccess: FeatureService = injectorAccess.injector.get(FeatureService);
    const { editor, nav, settings, activeJourney, account } = vm;

    const props = defineProps<{
        node: JourneyNode;
    }>();

    let nodeTypes: { [key: string]: { id: number; name: string } } = {};

    for (var type in NodeType) {
        if (typeof NodeType[type] === 'number') {
            nodeTypes[NodeType[type]] = {
                id: parseInt(NodeType[type]),
                name: type,
            };
        }
    }

    const typeMenuComponent = computed(() => {
        switch (NodeType[props.node.data.type]) {
            case 'Segment':
                return NodeTypeSegment;
            case 'Task':
                return NodeTypeTask;
            // case 'Condition':
            //    return NodeTypeCondition
            default:
                return;
        }
    });

    const resetTypeData = (val: any) => {
        let old_type = props.node.data.type;
        props.node.data.type = val;

        if (props.node.data.isInitial) activeJourney.startingNode = null;

        delete props.node.data.isInitial;
        delete props.node.data.startingSegment;

        props.node.data.conditions ||= [];

        if (val === NodeType.Segment) {
            props.node.data.ListIds = [];
            props.node.data.SegmentIds = [];
        } else {
            delete props.node.data.ListIds;
            delete props.node.data.SegmentIds;
        }

        // if (old_type === NodeType.Condition && val !== NodeType.Condition) {
        //    props.node.h = 70
        //    props.node.w = 70
        // }
        // else if (val === NodeType.Condition) {
        //    props.node.w = 100
        // }
    };

    // const getInheritedProps = (lines_fr: any) => {
    //    let lines = activeJourney.lines,
    //       nodes = activeJourney.nodes,
    //       segments: any = [],
    //       conditions: any = []

    //    const recFunc = (lns: any, res?: any) => {

    //       for (let lid in lns) {

    //          let prev_node = nodes[lines[lid].fr!]

    //          if (prev_node.data.type === NodeType.Condition) {
    //             let node_conditions = prev_node.data.conditions.map((c: any) => {
    //                let condition = {
    //                   nodeId: prev_node.id,
    //                   title: c.Type
    //                }
    //                return Object.assign(condition, c)
    //             })
    //             conditions = conditions.concat(node_conditions)
    //          }
    //          else if (prev_node.data.type !== NodeType.Condition) {
    //             segments.push({ nodeId: prev_node.id, SegmentId: prev_node.data.SegmentId })
    //             continue
    //          }

    //          Object.keys(prev_node.lnfr).length > 0 && recFunc(prev_node.lnfr)
    //       }
    //    }
    //    Object.keys(lines_fr).length > 0 && recFunc(lines_fr)

    //    return { segments, conditions }
    // }

    // const inheritedProps: any = computed(() => {
    //    let inherited = getInheritedProps(props.node.lnfr)
    //    return inherited
    // })
</script>

<template>
    <div>
        <BSFormInputText class="form-group" v-model="node.data.title" :label="$Resources.TitleLabel" :placeholder="$Resources.TitleLabel"></BSFormInputText>

        <BSFormInputTextarea
            class="form-group"
            v-model="node.data.description"
            :label="$Resources.DescriptionLabel"
            :placeholder="$Resources.DescriptionLabel"></BSFormInputTextarea>

        <template v-if="node.data.type === NodeType.Condition && node.data.var === NodeConditionVariation.Delay">
            <h3 class="h4 m-0-t">{{ $Resources.GenericDelayLabel }}</h3>

            <div class="form-group d-flex gap-lg">
                <div class="d-flex flex-direction-rowrev gap-sm align-items-center">
                    <label class="text-nowrap" :for="'delayDays-' + $.uid">{{ $Resources.DelayLabelDays }}</label>
                    <input
                        :id="'delayDays-' + $.uid"
                        class="form-control"
                        type="number"
                        @change="(ev: any) => (node.data.delay_d = parseInt(ev.target.value))"
                        :value="node.data.delay_d" />
                </div>

                <div class="d-flex flex-direction-rowrev gap-sm align-items-center">
                    <label class="text-nowrap" :for="'delayHours-' + $.uid">{{ $Resources.DelayLabelHours }}</label>
                    <input
                        :id="'delayHours-' + $.uid"
                        class="form-control"
                        type="number"
                        @change="(ev: any) => (node.data.delay_h = parseInt(ev.target.value))"
                        :value="node.data.delay_h || 0" />
                </div>

                <template v-if="featureAccess.hasFeature('BackOffice')">
                    <div class="d-flex flex-direction-rowrev gap-sm align-items-center">
                        <label class="text-nowrap" :for="'delayMinutes-' + $.uid">{{ $Resources.DelayLabelMinutes }}</label>
                        <input
                            :id="'delayMinutes-' + $.uid"
                            class="form-control"
                            type="number"
                            @change="(ev: any) => (node.data.delay_m = parseInt(ev.target.value))"
                            :value="node.data.delay_m || 0" />
                    </div>
                </template>
            </div>
        </template>

        <!-- <transition name="component-roll" mode="out-in"> -->
        <component :is="typeMenuComponent" :data="node.data" :id="node.id"></component>
        <!-- </transition> -->

        <!-- <NodeTypeCondition :data="node.data" :id="node.id" style="margin-top: 3rem" /> -->

        <!-- <h3 class="title mt-l">Inherited properties</h3>
      <h4>Accumulated between tasks and cannot be overriden.</h4> -->

        <!-- inherited segment -->
        <!-- <div class="inherited-prop-card mt-md" v-for="id in inheritedProps?.segments">
         <div class="input-wrapper align-fl-content-sb mt-xs mb-xs">
            <h3>{{ account.lists.contactLists[id].Name }}</h3>
            <h4>{{ account.lists.contactLists[id].ContactCount }}</h4>
         </div>
      </div> -->
    </div>
</template>

<style>
    .example {
        height: 2rem;
        width: 10rem;
        padding: 0.3rem 0.6rem;
        background-color: #dcdcdc;
        text-align: center;
        margin: 0.3rem 0;
        border-radius: 2px;
    }
</style>
